/**
 * PathwayEnrichmentViz - Pathway enrichment bubble plot visualization for chat mode.
 *
 * Displays enriched biological pathways with fold enrichment vs significance,
 * bubble size indicates gene count.
 */

import React from 'react';
import Plot from 'react-plotly.js';
import InfoTooltip from './InfoTooltip';

interface PathwayEnrichmentVizProps {
  data: {
    type: 'pathway_enrichment';
    pathways: Array<{
      name: string;
      fold_enrichment: number;
      fdr: number;
      gene_count: number;
      genes?: string[];
      database?: string;
    }>;
    metadata?: {
      gene: string;
      disease: string;
    };
  };
  onAction?: (action: string, params?: any) => void;
}

export const PathwayEnrichmentViz: React.FC<PathwayEnrichmentVizProps> = ({ data, onAction }) => {
  if (!data.pathways || data.pathways.length === 0) {
    return (
      <div className="origin-card mt-4 p-4">
        <p className="text-sm text-gray-500">No significant pathways found.</p>
      </div>
    );
  }

  // Filter out invalid pathways
  const validPathways = data.pathways.filter(
    p => p.fold_enrichment > 0 && p.fdr > 0 && p.fdr < 1
  );

  if (validPathways.length === 0) {
    return (
      <div className="origin-card mt-4 p-4">
        <p className="text-sm text-gray-500">No valid pathway data available.</p>
      </div>
    );
  }

  // Prepare plot data
  const xValues = validPathways.map(p => p.fold_enrichment);
  const yValues = validPathways.map(p => -Math.log10(p.fdr));
  const sizes = validPathways.map(p => p.gene_count);
  const labels = validPathways.map(p => p.name);
  const colors = yValues; // Color by significance

  // Create hover text
  const hoverText = validPathways.map(p =>
    `<b>${p.name}</b><br>` +
    `Fold Enrichment: ${p.fold_enrichment.toFixed(2)}<br>` +
    `FDR: ${p.fdr.toExponential(2)}<br>` +
    `Genes: ${p.gene_count}<br>` +
    (p.database ? `Database: ${p.database}` : '')
  );

  return (
    <div className="origin-card mt-4 p-4">
      <div className="mb-4">
        <h3 className="text-lg font-semibold mb-1 flex items-center gap-2">
          Pathway Enrichment
          <InfoTooltip title="Pathway Enrichment Plot" content="Bubble plot showing enriched biological pathways. X-axis (log scale): how many more pathway genes are present than expected by chance. Y-axis: statistical significance. Bubble size: number of genes from the network found in the pathway." position="right" width="w-72" />
        </h3>
        {data.metadata?.gene && (
          <p className="text-sm text-gray-400">
            Gene: <span className="font-mono">{data.metadata.gene}</span>
            {data.metadata.disease && ` • ${data.metadata.disease}`}
          </p>
        )}
        <p className="text-xs text-gray-500 mt-2">
          {validPathways.length} significant pathways (FDR &lt; 0.05)
        </p>
      </div>

      <Plot
        data={[{
          x: xValues,
          y: yValues,
          mode: 'markers',
          type: 'scatter',
          marker: {
            size: sizes,
            color: colors,
            colorscale: 'Viridis',
            showscale: true,
            colorbar: {
              title: { text: '-log10(FDR)' },
              thickness: 15,
              len: 0.5
            },
            sizemode: 'diameter',
            sizeref: Math.max(...sizes) / 50,
            line: {
              color: 'rgba(255, 255, 255, 0.5)',
              width: 1
            }
          },
          text: labels,
          hovertext: hoverText,
          hoverinfo: 'text'
        }]}
        layout={{
          xaxis: {
            title: 'Fold Enrichment',
            type: 'log',
            gridcolor: 'rgba(75, 85, 99, 0.4)',
            color: '#9ca3af',
          },
          yaxis: {
            title: '-log10(FDR)',
            gridcolor: 'rgba(75, 85, 99, 0.4)',
            color: '#9ca3af',
          },
          height: 450,
          paper_bgcolor: 'transparent',
          plot_bgcolor: 'transparent',
          font: { color: '#d1d5db' },
          hovermode: 'closest',
          margin: { l: 70, r: 40, t: 40, b: 70 }
        }}
        config={{ responsive: true, displayModeBar: false }}
      />

      {/* Axis legend */}
      <div className="flex flex-wrap gap-4 mt-2 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          X: Fold Enrichment (log)
          <InfoTooltip title="Fold Enrichment" content="Observed / expected gene overlap ratio (log scale). Higher = more enriched than chance. Values > 2x are generally meaningful." />
        </span>
        <span className="flex items-center gap-1">
          Y: -log10(FDR)
          <InfoTooltip title="-log10(FDR)" content="Statistical significance transformed to positive scale. Values > 1.3 correspond to FDR < 0.05 (significant). Higher = more significant." />
        </span>
        <span className="flex items-center gap-1">
          Bubble size: gene count
          <InfoTooltip title="Bubble Size" content="Number of genes from the network found in this pathway. Larger bubbles = more network genes overlap with the pathway." />
        </span>
      </div>

      {/* Top pathways list */}
      <div className="mt-4">
        <h4 className="text-sm font-semibold mb-2">Top Pathways</h4>
        <div className="space-y-1 max-h-[200px] overflow-auto">
          {validPathways.slice(0, 10).map((pathway, idx) => (
            <div key={idx} className="flex items-center justify-between text-xs p-2 bg-gray-800 rounded hover:bg-gray-700">
              <span className="flex-1 truncate" title={pathway.name}>
                {pathway.name}
              </span>
              <span className="ml-2 font-mono text-gray-400">
                {pathway.gene_count} genes
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PathwayEnrichmentViz;
